import React from "react";
import { Box, Typography, styled } from "@mui/material";
import { formatCOP } from "../../utils/format";
import { CART_TEXTS } from "../../pages/shared/constants";
import { TotalRow } from "../shared/StyledComponents";

/**
 * The money, everywhere it is shown.
 *
 * One component because the cart drawer, the checkout page and the order summary must not disagree
 * about what a total means — and with quote lines in play they easily could. The rule this encodes:
 *
 *   **A total that omits a line says so.** When `quoteItemCount` is non-zero the label becomes
 *   "Total parcial" and the count is printed underneath. The number does not change; the claim
 *   made about it does (docs/ordering-flow/README.md §3.4).
 */

const Line = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
});

const QuoteNotice = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(0.75, 1.25),
  borderRadius: theme.spacing(0.75),
  color: theme.palette.purchase.quote,
  backgroundColor: theme.palette.action.hover,
  fontWeight: 600,
}));

type CartTotalsProps = {
  itemsSubtotal: number;
  quoteItemCount: number;
  /** Omitted until the shopper has chosen how the order gets to them. */
  deliveryFee?: number | null;
};

const CartTotals: React.FC<CartTotalsProps> = ({ itemsSubtotal, quoteItemCount, deliveryFee }) => {
  const hasDelivery = typeof deliveryFee === "number";
  const total = itemsSubtotal + (hasDelivery ? deliveryFee : 0);
  const isPartial = quoteItemCount > 0;

  return (
    <Box>
      {/* The subtotal line is redundant with the total when there is no fee yet, so it is not
          shown — two identical numbers stacked read as an error. */}
      {hasDelivery && (
        <>
          <Line>
            <Typography variant="body2">{CART_TEXTS.subtotalLabel}</Typography>
            <Typography variant="body2">{formatCOP(itemsSubtotal)}</Typography>
          </Line>
          <Line>
            <Typography variant="body2">{CART_TEXTS.deliveryLabel}</Typography>
            <Typography variant="body2">{formatCOP(deliveryFee)}</Typography>
          </Line>
        </>
      )}

      <TotalRow>
        <Typography variant="h6" fontWeight="bold">
          {isPartial ? CART_TEXTS.partialTotal : CART_TEXTS.total}
        </Typography>
        <Typography variant="h6" fontWeight="bold" color="success.main">
          {formatCOP(total)}
        </Typography>
      </TotalRow>

      {isPartial && (
        <QuoteNotice variant="body2">{CART_TEXTS.quoteNotice(quoteItemCount)}</QuoteNotice>
      )}
    </Box>
  );
};

export default CartTotals;
